import React, { useEffect, useContext, useMemo } from 'react';
import { string, func, bool, arrayOf } from 'prop-types';
import classNames from 'classnames';
import { LayoutGroup, LayoutManager } from '@thd-olt-component-react/layout-manager';
import { ExperienceContext, useStoreId } from '~/@thd-nucleus/experience-context';
import { useProductAddOnsFlow } from '../hooks/useProductAddOnsFlow';
import { HomeDepotProtectionPlan } from './add-ons/hdpp/HomeDepotProtectionPlan';
import { AttachLabor } from './add-ons/attach-labor/AttachLabor';
import { ProReferral } from './add-ons/pro-referral/ProReferral';
import { DualPath } from './add-ons/dual-path/DualPath';
import { SAMDualPath } from './add-ons/SAM-dual-path/SAMDualPath';
import { VirtualAssistance } from './add-ons/virtual-assistance/VirtualAssistance';
import { displayAttachLabor as attachLaborEligible } from '../utils/product-add-on-utils';
import { dataModel } from './dataModel';
import styles from './product-add-ons.scss';
import './tooltip.scss';

const ProductAddOns = ({
  itemId, fulfillment, availableFulfillments, onChange, showCards, hideProReferral
}) => {

  const expData = useContext(ExperienceContext);
  const { channel } = expData;

  const storeId = useStoreId();

  const {
    loading,
    data,
    isProtectionPlan,
    isProReferral,
    isAssembly,
    isDualPath,
    isInstallationSku,
    inStoreAssemblyAvailable,
    isSAMDualPath,
    isVirtualAssistance
  } = useProductAddOnsFlow({ itemId, storeId });

  const getCategory = (serviceName) => {
    const PR_INSTALL = 'PRInstall';
    const PAINTER = 'Painter';
    const ASSEMBLY = 'Assembly';

    if (serviceName === 'interior_painting' || serviceName === 'exterior_painting') return PAINTER;
    if (serviceName === 'barbeque_assembly' || serviceName === 'outdoor_furniture_assembly') return ASSEMBLY;

    return PR_INSTALL;
  };

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-add-ons.ready');
  }, []);

  const { installProcess, priceIncludes } = useMemo(() => {
    const doItForMe = data?.serviceAddOns?.attachLabor?.installSkus?.[0]?.doItForMe;
    return {
      priceIncludes: (doItForMe?.priceIncludes || []).filter((priceInclude) => priceInclude.shortText),
      installProcess: (doItForMe?.installProcess || []).filter((installProcessItem) => installProcessItem.longText)
    };
  }, [data?.serviceAddOns?.attachLabor?.installSkus?.[0]?.doItForMe]);

  if (!data || loading) {
    return null;
  }

  if (!data?.protectionPlan && !data?.serviceAddOns && !isSAMDualPath && !isVirtualAssistance) {
    return null;
  }

  const { price, termLength, detailsUrl, itemId: protectionPlanItemId } = data?.protectionPlan || {};

  const { installationProgramCategory } = data?.serviceAddOns?.sellFurnishInstall || {};
  const { serviceName } = data?.serviceAddOns?.proReferrals?.[0] || {};
  const { attachLabor } = data?.serviceAddOns || {};
  const installSkus = attachLabor?.installSkus?.[0] || {};
  const { serviceType, installSku } = installSkus || {};
  const { price: installRetailPrice } = attachLabor?.installSkus?.[0]?.installRetailPrice || {};

  const modelNumber = data?.product?.identifiers?.modelNumber || {};
  const { leadGenUrl } = data?.product?.details?.installation || {};
  const consultationType = data?.product?.info?.consultationType || null;

  const type = serviceType?.toLowerCase();
  const displayAttachLabor = attachLaborEligible({
    isInstallationSku,
    inStoreAssemblyAvailable,
    availableFulfillments,
    currentFulfillment: fulfillment
  });

  let cx = classNames.bind(styles);

  const wrapperClasses = cx({ 'product-add-ons': !showCards, 'product-add-ons--card-background': showCards });
  const cardClasses = cx({ 'product-add-ons__card': showCards });

  return (
    <section className={wrapperClasses}>
      {displayAttachLabor
        && (
          <div className={cardClasses}>
            <AttachLabor
              channel={channel}
              onChange={onChange}
              itemId={installSku}
              type={type}
              price={installRetailPrice}
              installProcess={installProcess}
              priceIncludes={priceIncludes}
              fulfillment={fulfillment}
              availableFulfillments={availableFulfillments}
              isInstallationSku={isInstallationSku}
              inStoreAssemblyAvailable={inStoreAssemblyAvailable}
            />
          </div>
        )}

      {isDualPath
        && (
          <div className={cardClasses}>
            <DualPath
              channel={channel}
              onChange={onChange}
              itemId={itemId}
              category={installationProgramCategory}
              serviceUrl={leadGenUrl}
            />
          </div>
        )}

      {isSAMDualPath
        && (
          <div className={cardClasses}>
            <SAMDualPath
              channel={channel}
              onChange={onChange}
              itemId={itemId}
              modelNumber={modelNumber}
            />
          </div>
        )}

      {isVirtualAssistance
        && (
          <div id="mva-buybox" className={cardClasses}>
            <LayoutManager name="product-add-ons" useContextForProps>
              <LayoutGroup name="virtual-assistance">
                <VirtualAssistance
                  channel={channel}
                  onChange={onChange}
                  itemId={itemId}
                  consultationType={consultationType}
                />
              </LayoutGroup>
            </LayoutManager>
          </div>
        )}

      {(!hideProReferral && !displayAttachLabor && (isProReferral || isAssembly))
        && (
          <div className={cardClasses}>
            <ProReferral
              onChange={onChange}
              channel={channel}
              category={getCategory(serviceName)}
              isAssembly={isAssembly}
            />
          </div>
        )}

      {isProtectionPlan
        && (
          <div className={cardClasses}>
            <HomeDepotProtectionPlan
              channel={channel}
              onChange={onChange}
              itemId={protectionPlanItemId}
              price={price}
              term={termLength}
              url={detailsUrl}
            />
          </div>
        )}
    </section>
  );
};

ProductAddOns.displayName = 'ProductAddOns';

ProductAddOns.defaultProps = {
  availableFulfillments: [],
  fulfillment: null,
  hideProReferral: false,
  showCards: false
};
ProductAddOns.propTypes = {
  /** Product Number */
  itemId: string.isRequired,
  /** Function to call when checkbox is clicked */
  onChange: func.isRequired,
  /** Fulfillment method selected */
  fulfillment: string,
  /** List of currently available fulfillment types  */
  availableFulfillments: arrayOf(string),
  /** Boolean to remove the Pro referral component */
  hideProReferral: bool,
  showCards: bool
};

ProductAddOns.dataModel = dataModel;

export { ProductAddOns };
